import { Scene, Vector3, Color3, AbstractMesh } from '@babylonjs/core';
import { SplineConstraint } from './SplineConstraint';
import { CharacterConfig } from '../config/characters';
import { NPCController } from './NPCController';
import { FollowAI } from '../ai/NPCAIBehaviors';
import { CharacterAsset } from '../assets/CharacterLoader';
import { PhysicsManager } from './physics';
import { logger } from '../core/Logger';
import { Character } from './Character';
import { DebugRenderer } from '../core/DebugRenderer';

export interface PartyMemberConfig {
  id: string;
  characterConfig: CharacterConfig;
  initialT: number;
  minDistance: number;
  maxDistance: number;
}

export class PartyManager {
  private members: NPCController[] = [];
  private scene: Scene;
  private splineConstraint: SplineConstraint;
  private physicsManager?: PhysicsManager;
  private leader?: Character;
  private leaderMarker?: AbstractMesh;
  private leaderMarkerOffset: Vector3 | null = null;
  private readonly markerSize = new Vector3(0.35, 0.2, 0.35);
  private readonly markerColor = new Color3(1, 0, 1);
  private readonly markerVerticalPadding = 0.05;
  private debugRenderer: DebugRenderer;

  constructor(scene: Scene, splineConstraint: SplineConstraint, physicsManager?: PhysicsManager) {
    this.scene = scene;
    this.splineConstraint = splineConstraint;
    this.physicsManager = physicsManager;
    this.debugRenderer = DebugRenderer.getInstance(scene);
  }

  async addFollower(
    getTargetT: () => number,
    config: PartyMemberConfig,
    cachedAsset?: CharacterAsset
  ): Promise<NPCController> {
    const npc = new NPCController(
      this.scene,
      this.splineConstraint,
      config.characterConfig,
      config.id,
      this.physicsManager
    );
    
    if (cachedAsset) {
      npc.loadFromAsset(cachedAsset);
    } else {
      await npc.load();
    }
    
    npc.playerT = config.initialT;
    const splinePoint = this.splineConstraint.getPointAt(config.initialT);
    npc.setPosition(splinePoint);
    npc.setAIBehavior(
      new FollowAI(getTargetT, config.maxDistance, this.splineConstraint.getTotalLength(), config.minDistance)
    );
    
    // Set proper initial position above ground
    npc.character.position.copyFrom(splinePoint);
    npc.character.position.y = splinePoint.y + 2.0; // Start 2 units above spline
    
    // CRITICAL: Set initial facing direction BEFORE resetPhysics
    // NPC should face toward the player based on relative T position
    const initialPlayerT = getTargetT();
    const deltaT = initialPlayerT - config.initialT;
    const facingDirection = deltaT !== 0 ? Math.sign(deltaT) : 1;
    npc.setFacingDirection(facingDirection);
    
    // Reset physics to apply the rotation to spline tangent
    npc.resetPhysics();
    
    logger.npc.log(`🎯 NPC ${config.id} spawned at T=${config.initialT.toFixed(3)}`);
    this.members.push(npc);
    return npc;
  }

  update(deltaTime: number) {
    this.updateLeaderMarker();
    for (const npc of this.members) {
      npc.update(deltaTime);
    }
  }

  getFollowers(): NPCController[] {
    return this.members;
  }

  removeFollower(npc: NPCController): void {
    const index = this.members.indexOf(npc);
    if (index > -1) {
      this.members.splice(index, 1);
    }
  }

  /**
   * Replace a follower in-place, preserving update order.
   * Useful for party leader swaps where the old leader becomes an NPC.
   */
  replaceFollower(oldNpc: NPCController, newNpc: NPCController): void {
    const index = this.members.indexOf(oldNpc);
    if (index > -1) {
      this.members.splice(index, 1, newNpc);
      return;
    }

    // Fallback: if the old follower wasn't tracked (shouldn't happen), just add.
    this.members.push(newNpc);
  }

  clearFollowers(): void {
    for (const npc of this.members) {
      npc.dispose();
    }
    this.members = [];
    this.setLeader(null);
  }

  setLeader(leader: Character | null): void {
    if (this.leader === leader) {
      return;
    }

    this.leader = leader ?? undefined;
    const markerConfig = leader?.getCharacterConfig().activeMarker;
    this.leaderMarkerOffset = markerConfig?.offset
      ? new Vector3(markerConfig.offset.x, markerConfig.offset.y, markerConfig.offset.z)
      : null;

    if (!leader) {
      this.disposeLeaderMarker();
      return;
    }

    if (!this.leaderMarker) {
      const marker = this.debugRenderer.createBox(
        leader.character.position.clone(),
        this.markerSize,
        this.markerColor,
        false
      );
      marker.isPickable = false;
      marker.checkCollisions = false;
      this.leaderMarker = marker;
    }

    this.updateLeaderMarker();
  }

  updateLeaderMarker(): void {
    if (!this.leader || !this.leaderMarker) {
      return;
    }

    const capsuleHalfHeight = this.leader.character.ellipsoid?.y ?? 0.9;
    const targetPosition = this.leader.character.position.clone();
    targetPosition.y += capsuleHalfHeight + (this.markerSize.y * 0.5) + this.markerVerticalPadding;
    if (this.leaderMarkerOffset) {
      targetPosition.addInPlace(this.leaderMarkerOffset);
    }

    this.leaderMarker.position.copyFrom(targetPosition);
  }

  private disposeLeaderMarker(): void {
    if (!this.leaderMarker) {
      return;
    }
    this.debugRenderer.remove(this.leaderMarker);
    this.leaderMarker = undefined;
    this.leaderMarkerOffset = null;
  }
}
